import { useCms } from '@/hooks/useCms';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCategoryPageData } from '@/hooks/useCategoryPageData';

import { RxMagnifyingGlass, RxMixerHorizontal } from 'react-icons/rx';
import MetaTags from '@/components/MetaTags';
import Drawer from '@/components/elements/Drawer';
import ArticleCard from '@/components/ArticleCard';
import Pagination from '@/components/elements/Pagination';
import CategoryFilters from '@/components/CategoryFilters';
import ArticleTypeFilter from '@/components/ArticleTypeFilter';
import CategoryPageSkeleton from '@/components/skeletons/CategoryPageSkeleton';
import ArticleCardSkeleton from '@/components/skeletons/ArticleCardSkeleton';

import type { CMSContextType } from '@/types/util.types';

function CategoryPage() {
  const isMobile = useIsMobile();
  const { categories } = useCms() as CMSContextType;
  const { category, articles, articlesLoading, featuredArticle, featuredArticleLoading, pagination, fetchArticles } =
    useCategoryPageData();

  return articles ? (
    <>
      {category && (
        <MetaTags title={category.name} description={category.description || `Category: ${category.name}`} />
      )}

      <div className='md:container md:flex md:flex-col px-4 pb-8 mt-7 mx-auto'>
        <header className='flex items-center justify-between w-full mb-3'>
          <h1 className='md:hidden m-0 p-0 text-2xl'>Articles</h1>
          {isMobile && (
            <Drawer buttonContent={<RxMixerHorizontal />}>
              <CategoryFilters categories={categories} disabled={articlesLoading || featuredArticleLoading} />
            </Drawer>
          )}
        </header>

        <main className='relative md:grid grid-cols-[218px_1fr] lg:grid-cols-[286px_1fr] gap-4 xl:gap-6'>
          {!isMobile && (
            <CategoryFilters categories={categories} disabled={articlesLoading || featuredArticleLoading} />
          )}

          <div className='articles h-min-content w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-4 xl:gap-6 md:grid-rows-[repeat(auto-fit,minmax(30px,min-content))]'>
            {featuredArticle && !featuredArticleLoading ? (
              <ArticleCard article={featuredArticle} className='md:col-span-full' summaryClassName='md:line-clamp-4' />
            ) : isMobile ? (
              <ArticleCardSkeleton className='col-span-full' imagePosition='top' />
            ) : (
              <ArticleCardSkeleton className='col-span-full' summaryLineCount={3} />
            )}

            <ArticleTypeFilter
              className='md:col-span-full row-[1/2] md:row-auto mb-4 md:mb-0'
              disabled={articlesLoading || featuredArticleLoading}
            />

            {articles && !articlesLoading
              ? articles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    layout='noImage'
                    background={isMobile ? 'white' : 'grey'}
                  />
                ))
              : Array.from({ length: 6 }, (_, index) => (
                  <ArticleCardSkeleton background={isMobile ? 'white' : 'grey'} key={index} layout='noImage' />
                ))}

            {articles.length < 1 && !articlesLoading && (
              <div className='col-span-full rounded-md md:mt-0 text-center p-4 pt-5 pb-6 mt-4 border border-gray-200'>
                <RxMagnifyingGlass className='mx-auto h-10 w-10 bg-gray-200 rounded-full p-2' />
                <p className='text-xl font-bold mt-4'>No articles found</p>
                <p className='text-gray-500 mt-2 max-w-[480px] mx-auto'>
                  We couldn&apos;t find any articles matching your current filters. Try adjusting your search criteria.
                </p>
              </div>
            )}

            {pagination.pageCount > 1 && (
              <Pagination
                setPage={fetchArticles}
                page={pagination.page}
                pageCount={pagination.pageCount}
                className='col-span-full mt-6'
              />
            )}
          </div>
        </main>
      </div>
    </>
  ) : (
    <CategoryPageSkeleton />
  );
}

export default CategoryPage;
